import type { SystemRolePermissionId } from '@clockwise/shared';
import {
  ApiError,
  lookupPermission,
  normalizePermissions,
  type PermissionEntry,
} from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { useShowsUnits, useUnitsWhere } from '@/lib/business-units';
import { BusinessUnitField } from './business-unit-field';
import { PermissionsEditor } from './permissions-editor';

/**
 * Shared add/edit sheet for tenant-scoped catalog entities — system_roles,
 * service_roles, skills, departments. Every variant has a `name`; the
 * `code`, `description`, `permissions`, `kind` (boolean), and `category`
 * (single-select) fields are opt-in via props.
 *
 * The form's internal field names are stable (`name`, `code`,
 * `description`). When an entity's API field differs (service_roles
 * exposes the code as `shortCode`, not `code`), the caller passes
 * `serverToFormField` so the sheet can translate inbound field-error
 * paths back to the form's internal name when calling `form.setError`.
 * Outbound translation (form values → API body) is the caller's
 * responsibility in their `submit` function — that's where the
 * domain-specific shape lives.
 */

const CatalogFormSchema = z.object({
  name: z.string().min(1, 'Required').max(120),
  // Optional at the schema layer so this form works for variants with no
  // code field. When the caller opts the code field in via `codeField`,
  // the `onSubmit` handler enforces presence inline so the user gets a
  // "Required" error under the field — matching every other validator.
  code: z
    .string()
    .max(20, 'Up to 20 characters')
    .regex(/^[A-Za-z0-9_-]*$/, 'Letters, digits, _ or - only'),
  description: z.string().max(2000).optional().or(z.literal('')),
  // Opt-in `kind` — only variants that pass `kindField` render and
  // submit it. `.optional()` paired with the `kindEnabled` gate in
  // `mutationFn` means variants that don't render the picker won't
  // trip the resolver if a future code path forgets to seed `kind` in
  // `defaultValues`/`reset`. The Zod parsed type is `boolean | undefined`;
  // mutationFn already gates on `dirtyFields.kind` before reading.
  kind: z.boolean().optional(),
  // Opt-in via `unitField`: the Business Unit a NEW record goes into.
  businessUnitId: z.string().optional(),
  // Opt-in `category` — single-select from caller-supplied options, same
  // opt-in shape as `kind` but for a non-boolean choice (e.g. skills'
  // Category). `.optional()` for the same reason as `kind`: variants that
  // don't pass `categoryField` never render or submit it.
  category: z.string().optional(),
});
type CatalogFormValues = z.infer<typeof CatalogFormSchema>;

const FORM_FIELDS = ['name', 'code', 'description', 'kind', 'businessUnitId', 'category'] as const;

type CatalogFormField = (typeof FORM_FIELDS)[number];
function isCatalogFormField(s: string): s is CatalogFormField {
  return (FORM_FIELDS as readonly string[]).includes(s);
}

export interface CatalogEntity {
  id: string;
  name: string;
  /** Some variants store the code under a different key on the row
   *  (service_roles uses `shortCode`). The setup page maps to `code`
   *  via `entityToFormValues` before passing in. */
  code?: string;
  description?: string | null;
  permissions?: PermissionEntry[];
  /** Generic "kind" — boolean. Variants opt in via `kindField`. Maps
   *  to whatever the caller calls it on the wire (e.g. `internal` for
   *  service roles); the sheet doesn't know or care. */
  kind?: boolean;
  /** Generic single-select "category". Variants opt in via
   *  `categoryField`. `null`/missing renders as the placeholder
   *  (unselected) rather than a blank required field. */
  category?: string | null;
}

export interface CatalogSubmitBody {
  name: string;
  code?: string;
  description: string | null;
  permissions?: PermissionEntry[];
  /** Present only when the caller opted into `kindField`. */
  kind?: boolean;
  /** Present only when the caller opted into `unitField` and picked one. */
  businessUnitId?: string;
  /** Present only when the caller opted into `categoryField`. Empty
   *  string means "no category chosen" — the caller's `submit` maps
   *  that to `null` on the wire the same way it already maps an empty
   *  description. */
  category?: string;
}

export interface CodeFieldConfig {
  /** Display label, e.g. "Short code" or "Code". */
  label: string;
  /** Placeholder shown inside the input. */
  placeholder?: string;
  /** Required by domain convention (default true for backward compat). */
  required?: boolean;
}

/**
 * Opt-in two-option Select for a boolean "kind" field. The sheet doesn't
 * encode any specific semantics — the caller names the field, the labels,
 * and decides what `true` / `false` mean (e.g. service roles: internal vs
 * customer). Mirrors the products form's Kind picker visually.
 */
export interface KindFieldConfig {
  label: string;
  /** Helper text shown under the Select. */
  hint?: string;
  /** Display labels for the two options. The `true` label is shown when
   *  `kind === true`; the `false` label when `kind === false`. */
  trueLabel: string;
  falseLabel: string;
}

/**
 * Opt-in single-select for a non-boolean "category" field. Same shape of
 * opt-in as `KindFieldConfig`, but for a caller-supplied option list
 * instead of a fixed true/false pair — e.g. skills' Category dropdown.
 */
export interface CategoryFieldConfig {
  label: string;
  options: readonly { value: string; label: string }[];
  placeholder?: string;
  /** Helper text shown under the Select. */
  hint?: string;
  /** Required by domain convention (default false — "uncategorized" is a
   *  valid state for a catalog row, unlike the code field). */
  required?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Singular, lowercase — used in titles and toasts ("Edit X", "X saved"). */
  entityLabel: string;
  /** Entity being edited; null for create. */
  entity: CatalogEntity | null;
  /**
   * Create mode only: start the form from these values instead of blank —
   * e.g. Duplicate, which opens the Add sheet as a copy of another row. They
   * are the form's baseline, so closing an untouched copy doesn't prompt.
   */
  initial?: Omit<CatalogEntity, 'id'> | null;
  /** Backend call. Receives the same form body for create + edit;
   *  caller branches on `entity ? patch : create`. */
  submit: (body: CatalogSubmitBody) => Promise<unknown>;
  /** Query keys to invalidate after success. */
  invalidateKeys: readonly (readonly unknown[])[];
  /** Render the code field when set; omit for variants without one. */
  codeField?: CodeFieldConfig;
  /** Render the description field. Defaults true. */
  showDescription?: boolean;
  /** Render the permissions editor + include permissions in the submit body. */
  permissionsEditable?: boolean;
  /** Where the name must be unique, for the description line. Defaults to
   *  "your tenant"; a system role's name is unique within its business unit. */
  uniqueWithin?: string;
  /** Render the kind Select when set; omit for variants without one. */
  kindField?: KindFieldConfig;
  /** The record's Business Unit: picked when adding (starting from
   *  `defaultUnitId`) among the units where the caller's role holds `add`,
   *  shown fixed when editing (`current`) — a record stays in its unit — when
   *  they see these records (`view`) in more than one unit. Adding with
   *  `current` set (Duplicate: the copy joins its source's unit) shows it
   *  fixed too, rather than offering a choice the server would not honour. */
  unitField?: {
    defaultUnitId?: string;
    current?: string;
    view: SystemRolePermissionId;
    add: SystemRolePermissionId;
  };
  /** Render the category Select when set; omit for variants without one. */
  categoryField?: CategoryFieldConfig;
  /**
   * Translate a server-side field-error path to the form's internal
   * field name. Use when the API field name differs from the form's
   * (e.g. service_roles: `{ shortCode: 'code', internal: 'kind' }`).
   * Defaults to identity.
   */
  serverToFormField?: Partial<Record<string, CatalogFormField>>;
}

export function CatalogFormSheet({
  open,
  onOpenChange,
  entityLabel,
  entity,
  initial = null,
  submit,
  invalidateKeys,
  codeField,
  showDescription = true,
  permissionsEditable = false,
  uniqueWithin = 'your tenant',
  kindField,
  unitField,
  categoryField,
  serverToFormField,
}: Props) {
  const isEdit = entity !== null;
  // What the form starts from: the row being edited, else the create-mode seed.
  const start = entity ?? initial;
  const codeEnabled = Boolean(codeField);
  const codeRequired = codeField?.required ?? true;
  const kindEnabled = Boolean(kindField);
  // Hooks run unconditionally; without `unitField` nothing below reads them.
  const showsUnits = useShowsUnits(unitField?.view ?? 'system_role.view');
  const addUnits = useUnitsWhere(unitField?.add ?? 'system_role.edit');
  // The picker: adding, with more than one unit they may add to. With one,
  // the record goes there (`defaultUnitId`) and nothing is asked; with the
  // unit already decided (`current`), it is shown, not asked.
  const unitEnabled = Boolean(unitField) && !isEdit && !unitField?.current && addUnits.length > 1;
  const categoryEnabled = Boolean(categoryField);
  const categoryRequired = categoryField?.required ?? false;
  const queryClient = useQueryClient();

  const form = useForm<CatalogFormValues>({
    resolver: zodResolver(CatalogFormSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      kind: false,
      businessUnitId: '',
      category: '',
    },
  });

  // Permissions live in component state; the editor is a controlled
  // component that emits new arrays via setPermissions.
  const [permissions, setPermissions] = useState<PermissionEntry[]>([]);

  // Reconcile stored permissions against the current registry on load so a row
  // whose permission lost its scopes in a registry change (stale `scope` left
  // in the DB) doesn't round-trip an invalid scope and 400 on save. Also the
  // baseline for the dirty check, so normalization alone doesn't mark dirty.
  //
  // Platform-tier entries (e.g. `tenant.view`/`tenant.edit`, present on the
  // seeded Super Admin role via SUPER_ADMIN_GRANT) are stripped here too —
  // the API's `validatePermissions` rejects ANY platform-tier id in a
  // tenant-role PATCH body outright, it doesn't just ignore it. The backend
  // re-adds whatever platform-tier entries the role already had after
  // validating the (now platform-tier-free) submission — see
  // `SystemRoleService.patch`'s `preservedPlatform` — so dropping them here
  // is what actually lets that preservation logic run instead of 400ing
  // before it's reached. `PermissionsEditor` already hides them from the
  // checkbox list for the same reason; this is the other half; a value it
  // was handed already containing them, not just one it would render.
  const initialPermissions = useMemo(
    () =>
      permissionsEditable
        ? normalizePermissions(start?.permissions ?? []).filter(
            (e) => lookupPermission(e.permission)?.tier !== 'platform',
          )
        : [],
    [permissionsEditable, start?.permissions],
  );

  useEffect(() => {
    if (!open) return;
    form.reset({
      name: start?.name ?? '',
      code: start?.code ?? '',
      description: start?.description ?? '',
      kind: start?.kind ?? false,
      businessUnitId: unitField?.defaultUnitId ?? '',
      category: entity?.category ?? '',
    });
    setPermissions(initialPermissions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity?.id, initial]);

  const mutation = useMutation({
    mutationFn: (values: CatalogFormValues) => {
      const body: CatalogSubmitBody = {
        name: values.name,
        description: showDescription && values.description ? values.description : null,
      };
      if (codeEnabled) body.code = values.code;
      if (permissionsEditable) body.permissions = permissions;
      if (unitField && !isEdit && values.businessUnitId) {
        body.businessUnitId = values.businessUnitId;
      }
      // Only emit `kind` when the user actually toggled it. On edit
      // this prevents a no-op save from re-writing the value (and
      // stomping a concurrent flip by another user); on create the
      // caller's submit() can supply the default. The other fields
      // (name/code/description) are always sent because that's the
      // pre-existing contract — only `kind` is gated on dirty.
      if (kindEnabled && form.formState.dirtyFields.kind) {
        body.kind = values.kind;
      }
      // Unlike `kind`, `category` is always sent when enabled rather than
      // dirty-gated: it's a plain single-select with no "stomp a
      // concurrent flip" risk, and both create and edit want a definite
      // value (including explicitly clearing it back to "uncategorized").
      if (categoryEnabled) body.category = values.category;
      return submit(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? `${cap(entityLabel)} saved` : `${cap(entityLabel)} created`);
      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        let hadFieldHit = false;
        for (const fe of err.fields) {
          const formPath = serverToFormField?.[fe.path] ?? fe.path;
          if (!isCatalogFormField(formPath)) continue;
          // Don't route an error to a field that isn't rendered — the
          // user would see the "Please fix the highlighted fields"
          // toast with nothing actually highlighted (dead-letter loop).
          // Each opt-in field gates here so its inline error block in
          // the JSX (rendered conditionally below) stays the only path
          // an error can take.
          if (formPath === 'code' && !codeEnabled) continue;
          if (formPath === 'kind' && !kindEnabled) continue;
          if (formPath === 'businessUnitId' && !unitEnabled) continue;
          if (formPath === 'category' && !categoryEnabled) continue;
          form.setError(formPath, { message: fe.message });
          hadFieldHit = true;
        }
        if (hadFieldHit) {
          toast.error('Please fix the highlighted fields');
        } else {
          // Non-form-field errors (e.g. permission rows, or a field
          // that isn't rendered on this variant) — surface as toast
          // since per-row inline hint would clutter the grid.
          const first = err.fields[0];
          toast.error(first ? `${first.path}: ${first.message}` : err.message);
        }
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const onSubmit = form.handleSubmit((v) => {
    // "Required" for the code field lives here rather than in the Zod
    // schema so the schema can be shared with variants that omit the
    // field. setError keeps the message inline with every other validator.
    if (codeEnabled && codeRequired && !v.code) {
      form.setError('code', { message: 'Required' });
      return;
    }
    if (unitEnabled && !v.businessUnitId) {
      form.setError('businessUnitId', { message: 'Choose a Business Unit.' });
      return;
    }
    if (categoryEnabled && categoryRequired && !v.category) {
      form.setError('category', { message: 'Required' });
      return;
    }
    mutation.mutate(v);
  });
  const errors = form.formState.errors;

  // Permissions live outside the form, so fold their edits into the dirty
  // signal — otherwise changing only permissions would close without a prompt.
  const permissionsDirty =
    permissionsEditable && JSON.stringify(permissions) !== JSON.stringify(initialPermissions);
  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty || permissionsDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent
          className={`flex flex-col gap-0 ${permissionsEditable ? 'sm:max-w-2xl' : 'sm:max-w-md'}`}
        >
          <SheetHeader>
            <SheetTitle>{isEdit ? `Edit ${entityLabel}` : `Add ${entityLabel}`}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? `Update the ${entityLabel}. Name must be unique within ${uniqueWithin}.`
                : `Create a new ${entityLabel}. Name must be unique within ${uniqueWithin}.`}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4">
              {unitField && showsUnits && unitField.current ? (
                <BusinessUnitField
                  value={unitField.current}
                  view={unitField.view}
                  add={unitField.add}
                  hint={
                    isEdit
                      ? `A ${entityLabel} stays in its Business Unit.`
                      : `The copy is made in the same Business Unit as the original.`
                  }
                />
              ) : null}
              {unitField && unitEnabled ? (
                <BusinessUnitField
                  view={unitField.view}
                  add={unitField.add}
                  value={form.watch('businessUnitId') ?? ''}
                  onChange={(v) =>
                    form.setValue('businessUnitId', v, { shouldDirty: true, shouldValidate: true })
                  }
                  error={errors.businessUnitId?.message}
                  hint={`The ${entityLabel} belongs to this Business Unit.`}
                />
              ) : null}
              <Field
                id="name"
                label="Name"
                placeholder="Administrator"
                error={errors.name?.message}
                register={form.register('name')}
              />
              {codeField ? (
                <Field
                  id="code"
                  label={codeField.label}
                  placeholder={codeField.placeholder}
                  error={errors.code?.message}
                  register={form.register('code')}
                />
              ) : null}
              {showDescription ? (
                <div className="space-y-1.5">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    rows={3}
                    placeholder={`What this ${entityLabel} represents.`}
                    {...form.register('description')}
                  />
                  {errors.description?.message ? (
                    <p className="text-xs text-destructive">{errors.description.message}</p>
                  ) : null}
                </div>
              ) : null}

              {kindField ? (
                <div className="space-y-1.5">
                  <Label htmlFor="kind">{kindField.label}</Label>
                  <Select
                    value={form.watch('kind') ? 'true' : 'false'}
                    onValueChange={(v) =>
                      form.setValue('kind', v === 'true', { shouldDirty: true })
                    }
                  >
                    <SelectTrigger id="kind" className="w-full" aria-invalid={Boolean(errors.kind)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">{kindField.falseLabel}</SelectItem>
                      <SelectItem value="true">{kindField.trueLabel}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.kind?.message ? (
                    <p className="text-xs text-destructive">{errors.kind.message}</p>
                  ) : kindField.hint ? (
                    <p className="text-xs text-muted-foreground">{kindField.hint}</p>
                  ) : null}
                </div>
              ) : null}

              {categoryField ? (
                <div className="space-y-1.5">
                  <Label htmlFor="category">{categoryField.label}</Label>
                  <Select
                    value={form.watch('category') || undefined}
                    onValueChange={(v) =>
                      form.setValue('category', v, { shouldDirty: true, shouldValidate: true })
                    }
                  >
                    <SelectTrigger
                      id="category"
                      className="w-full"
                      aria-invalid={Boolean(errors.category)}
                    >
                      <SelectValue placeholder={categoryField.placeholder ?? 'Select…'} />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryField.options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category?.message ? (
                    <p className="text-xs text-destructive">{errors.category.message}</p>
                  ) : categoryField.hint ? (
                    <p className="text-xs text-muted-foreground">{categoryField.hint}</p>
                  ) : null}
                </div>
              ) : null}

              {permissionsEditable ? (
                <PermissionsEditor value={permissions} onChange={setPermissions} />
              ) : null}
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface FieldProps {
  id: CatalogFormField;
  label: string;
  placeholder?: string;
  error?: string;
  register: ReturnType<UseFormReturn<CatalogFormValues>['register']>;
}

function Field({ id, label, placeholder, error, register }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} placeholder={placeholder} aria-invalid={Boolean(error)} {...register} />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
